<script setup lang="ts">
import { nextTick, reactive, ref } from 'vue';
import {
  deviceColorLabel,
  deviceColors,
  deviceIconLabel,
  deviceIcons,
  groupLimit,
  nameLimit,
  type DeviceColor,
  type DeviceCustomization,
  type DeviceIcon,
} from '../lib/customization.js';
import { defaultIcon, deviceIconGlyph, type DirectoryDevice } from '../lib/directory.js';
import AppIcon from './AppIcon.vue';
import DeviceGlyph from './DeviceGlyph.vue';

/** Nickname, group, icon and colour for one device. Empty name or group means "use what was reported". */
const props = defineProps<{
  busy: boolean;
  saveDisabled?: boolean;
  groups: readonly string[];
  scope: string;
  error: string;
}>();
const emit = defineEmits<{
  save: [id: string, patch: { [K in keyof DeviceCustomization]?: DeviceCustomization[K] | '' }];
  reset: [id: string];
  close: [];
}>();

const dialog = ref<HTMLDialogElement>();
const nameInput = ref<HTMLInputElement>();
const target = ref<DirectoryDevice>();
const form = reactive<{ name: string; group: string; icon: DeviceIcon; color: DeviceColor }>({
  name: '',
  group: '',
  icon: 'monitor',
  color: 'mint',
});

async function open(device: DirectoryDevice) {
  target.value = device;
  form.name = device.name === device.reportedName ? '' : device.name;
  form.group = device.group === device.reportedGroup ? '' : device.group;
  form.icon = device.icon;
  form.color = device.color;
  dialog.value?.showModal();
  await nextTick();
  nameInput.value?.focus();
}
function close() {
  dialog.value?.close();
}
function save() {
  const device = target.value;
  if (!device) return;
  emit('save', device.id, {
    name: form.name.trim(),
    group: form.group.trim(),
    icon: form.icon === defaultIcon(device.platform) ? '' : form.icon,
    color: form.color === 'mint' ? '' : form.color,
  });
}
function reset() {
  if (target.value) emit('reset', target.value.id);
}
defineExpose({ open, close });
</script>

<template>
  <dialog ref="dialog" class="dialog" aria-labelledby="customize-title" @close="emit('close')">
    <form v-if="target" class="dialog-body" @submit.prevent="save">
      <header class="dialog-head">
        <div>
          <p class="eyebrow">Customize</p>
          <h2 id="customize-title">{{ target.reportedName }}</h2>
        </div>
        <button
          type="button"
          class="icon-button quiet"
          aria-label="Close customize"
          :disabled="props.busy"
          @click="close"
        >
          <AppIcon name="close" />
        </button>
      </header>
      <p>
        Names, groups and icons you set here are saved for {{ props.scope }}. They never change the
        device itself or who can reach it.
      </p>
      <p v-if="props.error" class="banner error" role="alert">{{ props.error }}</p>
      <div class="customize-preview">
        <DeviceGlyph
          :icon="form.icon"
          :color="form.color"
          :platform="target.reportedPlatform ?? target.platform"
          :os-version="target.osVersion"
          :presence="target.presence"
          size="lg"
        />
        <div class="customize-preview-text">
          <strong>{{ form.name.trim() || target.reportedName }}</strong>
          <span class="muted">{{ form.group.trim() || target.reportedGroup }}</span>
          <span class="device-host">{{ target.hostname }}</span>
        </div>
      </div>
      <label class="field">
        <span class="field-label">Name</span>
        <input
          ref="nameInput"
          v-model="form.name"
          :maxlength="nameLimit"
          :placeholder="target.reportedName"
          autocomplete="off"
        />
        <span class="field-help">Leave empty to use the reported name.</span>
      </label>
      <label class="field">
        <span class="field-label">Group</span>
        <input
          v-model="form.group"
          list="customize-groups"
          :maxlength="groupLimit"
          :placeholder="target.reportedGroup"
          autocomplete="off"
        />
        <datalist id="customize-groups">
          <option v-for="group in props.groups" :key="group" :value="group" />
        </datalist>
        <span class="field-help">
          A place, a company or a client. Devices in the same group are shown together. Groups never
          grant or restrict access.
        </span>
      </label>
      <fieldset class="picker icon-picker">
        <legend class="field-label">Icon</legend>
        <label v-for="icon in deviceIcons" :key="icon" :title="deviceIconLabel[icon]">
          <input v-model="form.icon" type="radio" name="device-icon" :value="icon" />
          <AppIcon :name="deviceIconGlyph[icon]" />
          <span class="sr-only">{{ deviceIconLabel[icon] }}</span>
        </label>
      </fieldset>
      <fieldset class="picker color-picker">
        <legend class="field-label">Color</legend>
        <label
          v-for="color in deviceColors"
          :key="color"
          :data-color="color"
          :title="deviceColorLabel[color]"
        >
          <input v-model="form.color" type="radio" name="device-color" :value="color" />
          <span class="swatch" aria-hidden="true" />
          <span class="sr-only">{{ deviceColorLabel[color] }}</span>
        </label>
      </fieldset>
      <footer class="dialog-actions">
        <button
          type="button"
          class="quiet"
          :disabled="props.busy || props.saveDisabled || !target.customized"
          @click="reset"
        >
          Reset to reported
        </button>
        <span class="session-spacer" />
        <button type="button" :disabled="props.busy" @click="close">Cancel</button>
        <button type="submit" class="primary" :disabled="props.busy || props.saveDisabled">
          Save
        </button>
      </footer>
    </form>
  </dialog>
</template>
